import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import { Check, X, ArrowRight, Heart } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { ChapterTag } from "./shared";
import { submitRSVP } from "../lib/api";

const spring = { duration: 0.6, ease: [0.16, 1, 0.3, 1] };

export default function Chapter7RSVP() {
  const [step, setStep] = useState("intro"); // intro | form | done
  const [attending, setAttending] = useState(true);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", guests: "1", dietary: "", message: "" });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const choose = (isAttending) => {
    setAttending(isAttending);
    setStep("form");
  };

  const celebrate = () => {
    const end = Date.now() + 800;
    const colors = ["#BBA680", "#656B45", "#E0D0C0", "#E9EADC"];
    (function frame() {
      confetti({ particleCount: 4, angle: 60, spread: 60, origin: { x: 0 }, colors });
      confetti({ particleCount: 4, angle: 120, spread: 60, origin: { x: 1 }, colors });
      if (Date.now() < end) requestAnimationFrame(frame);
    })();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Cuéntanos tu nombre, por favor");
      return;
    }
    setLoading(true);
    try {
      await submitRSVP({
        name: form.name.trim(),
        email: form.email.trim() || null,
        attending,
        guests: attending ? parseInt(form.guests, 10) : 0,
        dietary: attending ? form.dietary.trim() || null : null,
        message: form.message.trim() || null,
      });
      setStep("done");
      if (attending) celebrate();
    } catch (err) {
      toast.error("Algo salió mal. Inténtalo de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="relative bg-deep-black py-28 md:py-40" data-testid="chapter-7">
      <div className="mx-auto max-w-3xl px-6 md:px-16">
        <ChapterTag n={5} title="Confirmación" dark />

        <div className="relative min-h-[30rem] overflow-hidden rounded-2xl border border-ivory/10 bg-ivory/[0.03] p-8 backdrop-blur-xl md:p-14">
          <AnimatePresence mode="wait">
            {step === "intro" && (
              <motion.div
                key="intro"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={spring}
                className="flex flex-col items-center py-10 text-center"
              >
                <h2 className="max-w-xl font-serif text-4xl font-light leading-tight text-ivory md:text-6xl">
                  ¿Vas a celebrar <span className="italic text-gold">con nosotros?</span>
                </h2>
                <div className="mt-14 flex w-full max-w-md flex-col gap-4 sm:flex-row">
                  <button
                    data-testid="rsvp-attending-yes"
                    data-cursor="hover"
                    onClick={() => choose(true)}
                    className="group flex flex-1 items-center justify-center gap-3 rounded-full bg-gold px-8 py-4 text-sm uppercase tracking-[0.2em] text-deep-black transition-transform duration-300 hover:scale-[1.03]"
                  >
                    <Check className="h-4 w-4" /> ¡Claro que sí!
                  </button>
                  <button
                    data-testid="rsvp-attending-no"
                    data-cursor="hover"
                    onClick={() => choose(false)}
                    className="flex flex-1 items-center justify-center gap-3 rounded-full border border-ivory/25 px-8 py-4 text-sm uppercase tracking-[0.2em] text-ivory/80 transition-colors duration-300 hover:border-ivory/60"
                  >
                    <X className="h-4 w-4" /> Lamentablemente no podré
                  </button>
                </div>
              </motion.div>
            )}

            {step === "form" && (
              <motion.form
                key="form"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={spring}
                onSubmit={submit}
                className="space-y-7"
              >
                <p className="text-xs uppercase tracking-[0.3em] text-gold">
                  {attending ? "Qué alegría — un último detalle" : "Te vamos a extrañar"}
                </p>
                <div className="space-y-2">
                  <Label htmlFor="rsvp-name" className="text-xs uppercase tracking-[0.2em] text-ivory/60">
                    Tu nombre <span className="text-gold">*</span>
                  </Label>
                  <Input
                    id="rsvp-name"
                    data-testid="rsvp-name-input"
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Nombre completo"
                    required
                    className="border-0 border-b border-ivory/20 bg-transparent px-0 text-ivory placeholder:text-ivory/30 focus-visible:ring-0 rounded-none"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="rsvp-message" className="text-xs uppercase tracking-[0.2em] text-ivory/60">
                    Un mensaje para los novios <span className="normal-case text-ivory/30">(opcional)</span>
                  </Label>
                  <Textarea
                    id="rsvp-message"
                    data-testid="rsvp-message-input"
                    value={form.message}
                    onChange={set("message")}
                    placeholder="Escribe algo bonito..."
                    className="min-h-[90px] border border-ivory/20 bg-transparent text-ivory placeholder:text-ivory/30 focus-visible:ring-gold/40"
                  />
                </div>

                <div className="flex items-center justify-between pt-2">
                  <button
                    type="button"
                    onClick={() => setStep("intro")}
                    className="text-xs uppercase tracking-[0.2em] text-ivory/40 hover:text-ivory/80"
                  >
                    Regresar
                  </button>
                  <Button
                    type="submit"
                    data-testid="rsvp-submit-button"
                    disabled={loading}
                    className="group rounded-full bg-gold px-8 py-6 text-sm uppercase tracking-[0.2em] text-deep-black hover:bg-gold-soft"
                  >
                    {loading ? "Enviando..." : "Confirmar asistencia"}
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Button>
                </div>
              </motion.form>
            )}

            {step === "done" && (
              <motion.div
                key="done"
                data-testid="rsvp-success"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={spring}
                className="flex flex-col items-center py-16 text-center"
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring", stiffness: 200, damping: 12 }}
                  className="mb-8 flex h-20 w-20 items-center justify-center rounded-full border border-gold"
                >
                  <Heart className="h-8 w-8 text-gold" fill="#BBA680" strokeWidth={0} />
                </motion.div>
                <h2 className="font-serif text-4xl font-light text-ivory md:text-5xl">
                  {attending ? "¡Estás en la lista!" : "Gracias por avisarnos."}
                </h2>
                <p className="mt-4 max-w-md text-sm font-light text-ivory/50">
                  {attending
                    ? "No podemos esperar a celebrar este momento contigo."
                    : "Estarás con nosotros en espíritu — gracias por todo el cariño."}
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
